import * as THREE from 'three';
import { SurfaceType } from './SurfaceType';
import type { HeightField } from './HeightField';
import type { HeightSampler, NormalSampler, SurfaceTypeSampler, TerrainConfig } from './types';

/**
 * Terrain is a query surface, not just a mesh.
 *
 * Height/normal/surface-type are all sampled through injected functions
 * rather than hardcoded, so this class stays reusable across every course
 * without knowing anything about golf-specific concepts (fairway, hole,
 * bunker). Course/rendering-layer code wires its own data in via
 * setSurfaceTypeSampler once it loads; when a HeightField is supplied, its
 * height/normal sampling is used by default (and the mesh is genuinely
 * displaced to match), which is what makes "future sculpting" real: mutate
 * the HeightField, call refreshGeometry(), the visual mesh and every
 * getHeightAt() caller stay in sync automatically.
 */
export class Terrain {
  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.BufferGeometry;
  private readonly ownsMaterial: boolean;
  private readonly heightField: HeightField | null;
  private readonly resolution: number;

  private heightSampler: HeightSampler;
  private normalSampler: NormalSampler;
  private surfaceTypeSampler: SurfaceTypeSampler;

  constructor(config: TerrainConfig) {
    this.heightField = config.heightField ?? null;
    this.resolution = this.heightField?.resolution ?? config.resolution ?? 1;

    this.geometry = new THREE.PlaneGeometry(
      config.width,
      config.depth,
      this.resolution,
      this.resolution,
    );
    this.geometry.rotateX(-Math.PI / 2);
    const centerX = config.centerX ?? 0;
    const centerZ = config.centerZ ?? 0;
    this.geometry.translate(centerX, 0, centerZ);

    this.ownsMaterial = config.material === undefined;
    const material =
      config.material ??
      new THREE.MeshStandardMaterial({ color: 0x3c5a2e, roughness: 0.95, metalness: 0.0 });

    this.mesh = new THREE.Mesh(this.geometry, material);
    this.mesh.receiveShadow = true;
    this.mesh.name = 'Terrain';

    const baseHeight = config.baseHeight ?? 0;
    this.heightSampler =
      config.heightSampler ??
      this.heightField?.sampleHeight.bind(this.heightField) ??
      (() => baseHeight);
    this.normalSampler =
      config.normalSampler ??
      this.heightField?.sampleNormal.bind(this.heightField) ??
      (() => new THREE.Vector3(0, 1, 0));
    this.surfaceTypeSampler = config.surfaceTypeSampler ?? (() => SurfaceType.Rough);

    if (this.heightField) this.refreshGeometry();
  }

  get object3D(): THREE.Object3D {
    return this.mesh;
  }

  getHeightAt(x: number, z: number): number {
    return this.heightSampler(x, z);
  }

  getNormalAt(x: number, z: number): THREE.Vector3 {
    return this.normalSampler(x, z);
  }

  getSurfaceTypeAt(x: number, z: number): SurfaceType {
    return this.surfaceTypeSampler(x, z);
  }

  /** Replaces the height sampler. Prefer supplying a HeightField at construction over this for real geometry. */
  setHeightSampler(sampler: HeightSampler): void {
    this.heightSampler = sampler;
  }

  /** Replaces the normal sampler — pairs with setHeightSampler. */
  setNormalSampler(sampler: NormalSampler): void {
    this.normalSampler = sampler;
  }

  /**
   * Replaces the surface-type classification function. Course/Hole geometry
   * is the source of truth for fairway/rough/bunker/green boundaries;
   * rendering-layer code calls this so Terrain never needs to import Hole.
   */
  setSurfaceTypeSampler(sampler: SurfaceTypeSampler): void {
    this.surfaceTypeSampler = sampler;
  }

  /**
   * Re-reads the HeightField's grid into the mesh's vertex positions and
   * recomputes normals. This is the sculpting sync point: call this after
   * mutating the HeightField (raise/flatten/recess) to make the visual
   * mesh match. No-op if this Terrain wasn't built with a HeightField.
   */
  refreshGeometry(): void {
    if (!this.heightField) return;
    const heightField = this.heightField;

    const position = this.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < position.count; i++) {
      const worldX = position.getX(i);
      const worldZ = position.getZ(i);
      position.setY(i, heightField.sampleHeight(worldX, worldZ));
    }
    position.needsUpdate = true;
    this.geometry.computeVertexNormals();
  }

  dispose(): void {
    this.geometry.dispose();
    if (this.ownsMaterial) {
      const materials = Array.isArray(this.mesh.material)
        ? this.mesh.material
        : [this.mesh.material];
      materials.forEach((m) => m.dispose());
    }
  }

  // ---- Forward-looking extension points (not implemented this milestone) ----
  //
  // LOD: real courses will chunk terrain into tiles keyed by world-space
  // coordinates, each with multiple geometry resolutions swapped based on
  // camera distance. getHeightAt/getNormalAt are already the stable public
  // API those tiles would sample against — callers don't need to change
  // when chunking lands.
  //
  // Painting: surfaceTypeSampler already supports arbitrary classification
  // logic (see rendering/TerrainRenderer.ts for the current course-driven
  // implementation); a future paint tool would just produce a different
  // sampler function backed by an editable texture instead of procedural
  // region math.
}
